import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../api";
import "./pin-management.css";
import {Button} from "../buttons";


const PinManagementContainer = ( {children, className=""} ) => {
    return <div className={`pin-management-container main ${className}`}>
        {children}
    </div>
};

const Header = ( {title, info, className=""} ) => {
    return <div className={`pin-management-header ${className}`}>
        <h2>{title}</h2>
        <p>{info}</p>
    </div>
};

const Footer = ({children}) =>{
    return <div className="pin-management-footer">
    {children}
    </div>
}



const PciWidgetContainer = ( { url } ) => {
    return <div className="pci-widget-container">
        {url && (
            <iframe
                id="pci-widget-frame"
                src={url}
                className="pci-widget-iframe"
                allowtransparency="true"
                title="PIN Change Widget"
                sandbox="allow-scripts allow-same-origin allow-forms"
            />
        )}
    </div>
};



const PinManagement = ( ) => {
    const { t } = useTranslation();
    const { token } = useParams();
    const defaultWidgetUrl = new URLSearchParams(window.location.search).get("widgetUrl");
    const [ searchParams ] = useSearchParams();
    const api = API();

    const [ isLoading, setIsLoading ] = useState( !defaultWidgetUrl );
    const [ error, setError ] = useState( null );
    const [ widgetUrl, setWidgetUrl ] = useState( defaultWidgetUrl );
    const [ resetCode, setResetCode ] = useState( null );


    // Use ref to prevent multiple initializations
    const hasInitialized = useRef( false );

    const navigateHome = () => {
        window.location.href = `/${token}`
    }

    const initializePinChange = useCallback( async ( resetCodeFromParams ) => {
        // Prevent multiple calls if already initialized
        if ( hasInitialized.current && error ) {
            console.log( 'Already initialized with error, skipping...' );
            return;
        }

        try {
            setIsLoading( true );
            setError( null );

            if ( !resetCodeFromParams ) {
                setError( "Reset code is required" );
                setIsLoading( false );
                return;
            }
            setResetCode( resetCodeFromParams );

            // Get widget URL from backend API using reset code

            console.log( 'Calling API with token:', token, 'code:', resetCodeFromParams );
            const response = await api.getPinResetWidgetUrl( token, resetCodeFromParams );
            console.log( 'API response:', response );
            setIsLoading( false );

            if ( response.error ) {
                setError( response.error || "Failed to get widget URL" );
                return;
            }

            if ( !response.widgetUrl ) {
                setError( "Widget URL not received from server" );
                return;
            }

            setWidgetUrl( response.widgetUrl );

        } catch ( err ) {
            console.error( "Error initializing PIN change:", err );
            setError( "Failed to initialize PIN change" );
            setIsLoading( false );
        }
    }, [ token, api, error ] );

    useEffect( () => {
        // Prevent multiple initializations
        if ( hasInitialized.current ) {
            console.log( 'Already initialized, skipping...' );
            return;
        }
        if( defaultWidgetUrl ){
            return;
        }

        // Get reset code from URL parameters first
        const resetCodeFromParams = searchParams.get( "code" );
        console.log( 'Component mounted, token:', token, 'resetCode:', resetCodeFromParams );

        if ( resetCodeFromParams ) {
            console.log( 'Calling initializePinChange with resetCode' );
            hasInitialized.current = true; // Mark as initialized
            initializePinChange( resetCodeFromParams );
        } else {
            console.log( 'No reset code found, setting error' );
            hasInitialized.current = true; // Mark as initialized
            setError( "Reset code is required" );
            setIsLoading( false );
        }

        // Cleanup function
        return () => {
            console.log( 'Component unmounting, resetting initialization flag' );
            hasInitialized.current = false;
        };
    }, [] ); // Empty dependency array to run only once

    const handleWidgetMessage = ( event ) => {
        // Handle messages from the PCI widget iframe
        if ( !event.origin.endsWith(".nymcard.com") ) {
            console.warn( "Ignoring message from unknown origin:", event.origin, event.data );
            return;
        }

        console.warn( "Message from:", event.origin, event.data );
        try {
            const data = JSON.parse( event.data );

            if ( data.type === "SUCCESS" ) {
                // Redirect back to main card page
                setTimeout(navigateHome, 5000);
            } else if ( data.type === "ERROR" ) {
                setError( data.message || "Failed to change PIN" );
            }
        } catch ( err ) {
            setError( "Invalid message format from widget" );
            console.error( "Error parsing widget message:", err );
        }
    };



    useEffect( () => {
        // Add event listener for widget messages
        window.addEventListener( "message", handleWidgetMessage );

        return () => {
            window.removeEventListener( "message", handleWidgetMessage );
        };
    }, [] );

    if ( isLoading ) {
        return (
            <PinManagementContainer className={"loading"}>
                 <h2>{t( "giftcard.loadingPinChange" )}</h2>
                <p>{t( "giftcard.preparingSecurityWidget" )}</p>
            </PinManagementContainer>
        );
    }

    if ( error ) {
        return <PinManagementContainer>
                    <Header title={t( "giftcard.pinChangeError" )} info={error} className={"error"}/>
                    <Footer>
                        <Button label={t( "giftcard.backToCard" )} onClick={navigateHome} />
                    </Footer>
        </PinManagementContainer>
    }

    return (
        <PinManagementContainer>
            <Header title="" info={t( "giftcard.changePinDescription" )}/>
            <PciWidgetContainer url={widgetUrl}/>
            <Footer>
                <Button className={""} label={t( "giftcard.backToHomeScreen" )} onClick={navigateHome} />
                <Button className={"link"} label={t( "giftcard.cancel" )} onClick={navigateHome} />
            </Footer>
        </PinManagementContainer>
    );
};

export default PinManagement; 